import React from 'react';
import { Box, TextField, Chip, Autocomplete, Typography, Paper, useTheme } from '@mui/material';
import { Room } from '../types';

interface RoomSelectorProps {
  rooms: Room[];
  selectedRooms: string[];
  roomDetails: Record<string, { customName?: string; notes?: string }>;
  onRoomChange: (roomIds: string[], details: Record<string, { customName?: string; notes?: string }>) => void;
  onSubmit: () => void;
}

const RoomSelector: React.FC<RoomSelectorProps> = ({
  rooms,
  selectedRooms,
  roomDetails,
  onRoomChange,
  onSubmit
}) => {
  const theme = useTheme();

  const handleRoomChange = (_: any, newValue: Room[]) => {
    const newSelectedRooms = newValue.map(room => room.id);
    const newDetails = { ...roomDetails };
    
    // Remove details for unselected rooms
    Object.keys(newDetails).forEach(roomId => {
      if (!newSelectedRooms.includes(roomId)) {
        delete newDetails[roomId];
      }
    });
    
    onRoomChange(newSelectedRooms, newDetails);
  };

  const selectedRoomObjects = rooms.filter(room => selectedRooms.includes(room.id));

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 5, 
        mb: 4,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          color: 'primary.main',
          fontWeight: 600,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        Sélection des pièces
      </Typography>
      
      <Autocomplete
        multiple
        options={rooms}
        value={selectedRoomObjects}
        onChange={handleRoomChange}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Sélectionner les pièces"
            placeholder="Choisir des pièces"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '1.1rem',
                minHeight: '56px',
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.1rem',
                '&.Mui-focused': {
                  color: 'primary.main'
                }
              }
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option.name}
              {...getTagProps({ index })}
              onDelete={() => {
                const newSelectedRooms = selectedRooms.filter(id => id !== option.id);
                const newDetails = { ...roomDetails };
                delete newDetails[option.id];
                onRoomChange(newSelectedRooms, newDetails);
              }}
              sx={{
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                fontSize: '1rem',
                height: '32px',
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.contrastText,
                  fontSize: '1.2rem',
                  '&:hover': {
                    color: theme.palette.error.main,
                  },
                },
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
                transition: 'all 0.2s ease'
              }}
            />
          ))
        }
        sx={{
          '& .MuiAutocomplete-tag': {
            margin: 1,
          },
          '& .MuiAutocomplete-listbox': {
            fontSize: '1.1rem',
          }
        }}
      />
    </Paper>
  );
};

export default RoomSelector; 